interface TurnstileOptions {
    sitekey: string;
    theme: 'light' | 'dark' | 'auto';
    size: 'normal' | 'compact';
    callback?: ( token: string ) => void;
    'error-callback'?: () => void;
}

declare global {
    interface Window {
        turnstile: {
            render: ( container: HTMLElement, options: TurnstileOptions ) => string;
            reset: ( widgetId: string ) => void;
            remove: ( widgetId: string ) => void;
        };
    }
}

class TurnstileService {
    private widgets: Map<HTMLElement, string> = new Map();

    static load(): void {
        const service = new TurnstileService();
        service.init();
    }

    private init(): void {
        if ( document.readyState === 'loading' ) {
            document.addEventListener( 'DOMContentLoaded', () => this.setup() );
        } else {
            this.setup();
        }
    }

    private setup(): void {
        const containers = document.querySelectorAll( '.cf-turnstile' );
        if ( containers.length === 0 ) {
            return;
        }

        if ( typeof window.turnstile !== 'undefined' ) {
            this.renderAll( containers );
        } else {
            const checkTurnstile = () => {
                if ( typeof window.turnstile !== 'undefined' ) {
                    this.renderAll( containers );
                } else {
                    setTimeout( checkTurnstile, 100 );
                }
            };
            checkTurnstile();
        }

        // Listen for checkout updates
        jQuery( document ).on( 'updated_checkout', () => this.onCheckoutUpdate() );
    }

    private renderAll( containers: NodeListOf<Element> ): void {
        containers.forEach( ( container ) => {
            this.renderWidget( container as HTMLElement );
        } );
    }

    private renderWidget( container: HTMLElement ): void {
        // Skip if already rendered
        if ( this.widgets.has( container ) ) {
            return;
        }

        const options: TurnstileOptions = {
            sitekey: container.dataset.sitekey || '',
            theme: ( container.dataset.theme as any ) || 'light',
            size: ( container.dataset.size as any ) || 'normal',
            callback: ( token: string ) => this.onSuccess( token, container ),
            'error-callback': () => this.onError( container ),
        };

        const widgetId = window.turnstile.render( container, options );
        this.widgets.set( container, widgetId );
    }

    private onSuccess( token: string, container: HTMLElement ): void {
        this.setToken( token, container );
        this.hideError( container );
    }

    private onError( container: HTMLElement ): void {
        this.setToken( '', container );
        this.showError( container );
    }

    private setToken( token: string, container: HTMLElement ): void {
        const form = container.closest( 'form' ) || document.querySelector( 'form.checkout, form#order_review, form.woocommerce-form' );
        if ( !form ) {
            return;
        }

        const inputName = 'cf-turnstile-response';
        let input = form.querySelector( `input[name="${inputName}"]` ) as HTMLInputElement;

        if ( !input ) {
            input = document.createElement( 'input' );
            input.type = 'hidden';
            input.name = inputName;
            form.appendChild( input );
        }
        input.value = token;
    }

    private showError( container: HTMLElement ): void {
        const errorDiv = container.parentElement?.querySelector( '.cfw-turnstile-error' ) as HTMLElement;
        if ( errorDiv ) {
            errorDiv.style.display = 'block';
        }
    }

    private hideError( container: HTMLElement ): void {
        const errorDiv = container.parentElement?.querySelector( '.cfw-turnstile-error' ) as HTMLElement;
        if ( errorDiv ) {
            errorDiv.style.display = 'none';
        }
    }

    private onCheckoutUpdate(): void {
        setTimeout( () => {
            const containers = document.querySelectorAll( '.cf-turnstile' );
            containers.forEach( ( container ) => {
                const htmlContainer = container as HTMLElement;
                if ( !this.widgets.has( htmlContainer ) ) {
                    this.renderWidget( htmlContainer );
                }
            } );
        }, 100 );
    }

    public reset(): void {
        this.widgets.forEach( ( widgetId ) => {
            window.turnstile.reset( widgetId );
        } );
    }

    public resetWidget( container: HTMLElement ): void {
        const widgetId = this.widgets.get( container );
        if ( widgetId ) {
            window.turnstile.reset( widgetId );
        }
    }
}

export default TurnstileService;
